import axios from 'axios';
import { apiReachable, apiUnreachable } from '../features/connection/connectionSlice';
import { isApiUnreachable, isTimedOut } from '../utils/errors';
import { markRequestElapsed, markRequestStart } from './requestTiming';

let store;
export const injectStore = (_store) => {
  store = _store;
};

/**
 * The API as seen by somebody who is not signed in.
 *
 * The shipment-tracking pages are opened by a recipient from a link in an email
 * or a WhatsApp message. They have no account here, and the configured instance
 * in `./api` is built for the opposite case: it attaches `Authorization` from
 * `localStorage` to every request and sends any 401 to `/login`. On a public
 * page that means a stale token from a session somebody once opened on this
 * browser bounces a stranger to a login screen they have no business seeing.
 *
 * `withCredentials: false` is the part that is easy to miss. The tracking
 * endpoints declare no permissions, but DRF authenticates anyway, so a session
 * cookie riding along makes `SessionAuthentication` run its CSRF check — and a
 * signed-in operator opening a tracking link in the same browser gets a 403 for
 * their trouble. There is no session to send, so we do not send one.
 *
 * What it keeps is the reachability report: any answer proves the API is alive,
 * and a network-level failure is what puts the whole app behind
 * `ApiUnavailable`. That belongs to the app, not to the session. A search that
 * timed out is neither: the page says so in place.
 */
const publicApi = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8000/',
  withCredentials: false,
  timeout: 120000,
  headers: { 'Content-Type': 'application/json' },
});

publicApi.interceptors.request.use(markRequestStart);

publicApi.interceptors.response.use(
  (response) => {
    if (store) store.dispatch(apiReachable());
    return response;
  },
  (error) => {
    markRequestElapsed(error);
    if (store && !isTimedOut(error)) {
      store.dispatch(isApiUnreachable(error) ? apiUnreachable() : apiReachable());
    }
    return Promise.reject(error);
  }
);

export default publicApi;
